<script setup lang="ts">
import UiMicroAppItem from '@/components/LayoutV2/UiMicroAppItem.vue';
import { useMicroAppItemActions } from '@/components/LayoutV2/useMicroAppItemAction';
import { NIcon } from '@nio-fe/icon';
import { $confirm } from '@nio-fe/lego';
import { noticeMicro } from '@/utils/messageHandler';
import useGlobalStore from '@/store/globalStore';

const props = withDefaults(
  defineProps<{
    title?: string;
    activated?: boolean;
    icon: string;
    type: string;
    microAppName: string;
    size: string;
    badge?: string | number;
    brand?: string;
  }>(),
  {
    icon: 'task',
  }
);

const {
  handleGoToHomepage,
  handlePin,
  handleUnpin,
  handleClickMicroApp,
  handleCloseMicro,
  couldGoDown,
  couldGoUp,
  handleToBottom,
  handleToTop,
  handleSort,
} = useMicroAppItemActions();

const globalStore = useGlobalStore();

const handleClearUnread = () => {
  $confirm('确定清除未读标记？此操作将不影响加急消息标记', '清除未读', {
    type: 'warning',
    okText: '确定',
    cancelText: '取消',
  }).then((close) => {
    globalStore.getAvailableBrands().forEach((brand) => {
      noticeMicro('im', { action: 'clearAllUnreadMsg', brand });
    });
    close();
  });
};

const handleCloseSideItem = () => {
  const microAppName = props.microAppName;
  handleCloseMicro(microAppName);
};

const handleClick = () => {
  handleClickMicroApp(props.microAppName);
};
</script>

<template>
  <ui-micro-app-item
    class="micro-app-item"
    :title="title"
    :activated="activated"
    :icon="icon"
    :size="size"
    @click="handleClick"
  >
    <template #icon-affix>
      <n-badge
        v-if="!!badge"
        class="badge"
        :num-max="99"
        type="num"
        :content="badge"
      >
        <span></span
        ><!---->
      </n-badge>
      <n-icon
        name="urgent_filled"
        color="red"
        class="alps-icon"
        v-if="brand === 'alps'"
      />
    </template>
    <template #action>
      <n-dropdown align="right" trigger="click">
        <n-button
          icon="ellipsis"
          :size="'mini'"
          type="text"
          kind="neutral"
          :class="{ 'icon-button': true, 'icon-button--mini': size === 'mini' }"
        ></n-button>
        <n-dropdown-menu>
          <n-dropdown-item
            v-if="type === 'temporary'"
            icon="pin"
            @click="handlePin(microAppName)"
            v-tit-tracker:click="{
              button_group: '应用菜单',
              button_name: '固定应用',
              app_name: microAppName,
              trackName: 'FrameClick',
              requirement_id: 754,
              track_sign: 'aplatform.b2862.c2866.click.FrameClick',
            }"
            >固定应用</n-dropdown-item
          >
          <n-dropdown-item
            v-if="type === 'frequently'"
            icon="pinned"
            @click="handleUnpin(microAppName)"
            v-tit-tracker:click="{
              button_group: '应用菜单',
              button_name: '取消固定',
              app_name: microAppName,
              trackName: 'FrameClick',
              requirement_id: 754,
              track_sign: 'aplatform.b2862.c2866.click.FrameClick',
            }"
            >取消固定应用</n-dropdown-item
          >
          <n-dropdown-item
            icon="home"
            v-tit-tracker:click="{
              button_group: '应用菜单',
              button_name: '返回首页',
              app_name: microAppName,
              trackName: 'FrameClick',
              requirement_id: 754,
              track_sign: 'aplatform.b2862.c2866.click.FrameClick',
            }"
            @click="handleGoToHomepage(microAppName)"
            >返回应用首页</n-dropdown-item
          >
          <n-dropdown-item
            icon="clear_two"
            v-if="microAppName === 'message'"
            v-tit-tracker:click="{
              button_group: '应用菜单',
              button_name: '清除未读',
              app_name: microAppName,
              trackName: 'FrameClick',
              requirement_id: 754,
              track_sign: 'aplatform.b2862.c2866.click.FrameClick',
            }"
            @click="handleClearUnread"
            >清除未读</n-dropdown-item
          >
          <n-dropdown-item
            v-if="type === 'temporary'"
            icon="close"
            v-tit-tracker:click="{
              button_group: '应用菜单',
              button_name: '关闭',
              app_name: microAppName,
              trackName: 'FrameClick',
              requirement_id: 754,
              track_sign: 'aplatform.b2862.c2866.click.FrameClick',
            }"
            @click="handleCloseSideItem"
            >关闭应用</n-dropdown-item
          >
          <n-dropdown-item class="sort-wrapper" v-if="type === 'frequently'">
            <n-button
              size="small"
              icon="top"
              type="text"
              kind="neutral"
              v-tit-tracker:click="{
                button_group: '排序',
                button_name: '置顶',
                app_name: microAppName,
                trackName: 'FrameClick',
                requirement_id: 754,
                track_sign: 'aplatform.b2862.c2866.click.FrameClick',
              }"
              v-tooltip="couldGoUp(microAppName) ? '置顶应用' : '应用已置顶'"
              :disabled="!couldGoUp(microAppName)"
              @click="handleToTop(microAppName)"
            ></n-button>
            <n-button
              size="small"
              icon="arrow_up"
              type="text"
              kind="neutral"
              v-tit-tracker:click="{
                button_group: '排序',
                button_name: '向上',
                app_name: microAppName,
                trackName: 'FrameClick',
                requirement_id: 754,
                track_sign: 'aplatform.b2862.c2866.click.FrameClick',
              }"
              v-tooltip="couldGoUp(microAppName) ? '向上调整' : '应用已置顶'"
              @click="handleSort(microAppName, 'up')"
              :disabled="!couldGoUp(microAppName)"
            ></n-button>
            <n-button
              size="small"
              icon="arrow_down"
              type="text"
              kind="neutral"
              v-tit-tracker:click="{
                button_group: '排序',
                button_name: '向下',
                app_name: microAppName,
                trackName: 'FrameClick',
                requirement_id: 754,
                track_sign: 'aplatform.b2862.c2866.click.FrameClick',
              }"
              v-tooltip="couldGoDown(microAppName) ? '向下调整' : '应用已置底'"
              @click="handleSort(microAppName, 'down')"
              :disabled="!couldGoDown(microAppName)"
            ></n-button>
            <n-button
              size="small"
              icon="top"
              type="text"
              kind="neutral"
              class="bottom-btn"
              v-tit-tracker:click="{
                button_group: '排序',
                button_name: '置底',
                app_name: microAppName,
                trackName: 'FrameClick',
                requirement_id: 754,
                track_sign: 'aplatform.b2862.c2866.click.FrameClick',
              }"
              v-tooltip="couldGoDown(microAppName) ? '置底应用' : '应用已置底'"
              :disabled="!couldGoDown(microAppName)"
              @click="handleToBottom(microAppName)"
            ></n-button>
          </n-dropdown-item>
        </n-dropdown-menu>
      </n-dropdown>
    </template>
  </ui-micro-app-item>
</template>

<style scoped lang="scss">
.icon-button {
  margin-right: 0;
  :deep(.n-icon) {
    color: var(--tit-sidebar-menu-label-color);
  }
  &:hover {
    background: var(--tit-sidebar-menu-background-select) !important;
  }

  &--mini {
    margin-right: 2px;
  }
}

:deep(.micro-app-list-item__title) {
  max-width: 83px;
}
.micro-app-item {
  &:hover {
    :deep(.micro-app-list-item__title) {
      max-width: 60px;
    }
    :deep(.micro-app-list-item__fonticon) {
      .n-icon {
        color: var(--tit-sidebar-menu-icon-color-select) !important;
      }
    }
  }
  &.micro-app-list-item--selected {
    :deep(.micro-app-list-item__title) {
      max-width: 60px;
    }
    :deep(.micro-app-list-item__action) {
      display: block;
    }
    :deep(.micro-app-list-item__fonticon) {
      .n-icon {
        color: var(--tit-sidebar-menu-icon-color-select) !important;
      }
    }
  }
}
.badge {
  position: absolute;
  right: 0;
  top: 0;
}
.alps-icon {
  position: absolute;
  left: -6px;
  bottom: -6px;
}
.sort-wrapper {
  padding: 8px 12px;
  padding-bottom: 0;
  border-top: 1px solid #e6e7ec;
  &:hover {
    background-color: transparent !important;
  }
  :deep(.n-dropdown-item__content) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .bottom-btn {
    transform: rotate(180deg);
  }
}
</style>
